import React from "react";
import { FiChevronRight, FiPhone, FiEdit2 } from "react-icons/fi";
import {
  getLoanBalance,
  getLoanPaid,
  getLoanPrincipal,
  getLoanStatusLabel,
  getLoanTypeLabel,
} from "../../utils/LoanConverter";

const fmtRs = (n) =>
  `Rs ${Number(n || 0).toLocaleString("en-PK", { maximumFractionDigits: 0 })}`;

const formatDate = (unix) => {
  if (!unix) return "—";
  const d = new Date(unix * 1000);
  const day = String(d.getDate()).padStart(2, "0");
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const year = String(d.getFullYear()).slice(-2);
  return `${day}/${month}/${year}`;
};

const getInitials = (name) => {
  const parts = String(name || "?").trim().split(/\s+/);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return String(name || "?").slice(0, 2).toUpperCase();
};

const statusStyles = {
  Open: "bg-dangerSoft text-danger border-danger/20",
  Partial: "bg-warningSoft text-warning border-warning/20",
  Settled: "bg-successSoft text-success border-success/20",
};

const LoanPartyCard = ({ row, onOpen, onEdit }) => {
  const balance = getLoanBalance(row);
  const principal = getLoanPrincipal(row);
  const paid = getLoanPaid(row);
  const status = getLoanStatusLabel(row);
  const statusClass = statusStyles[status] || statusStyles.Open;
  const paidPct =
    principal > 0 ? Math.min(100, Math.round((paid / principal) * 100)) : 0;

  return (
    <article className="group relative w-full rounded-2xl bg-card border border-line shadow-soft hover:shadow-card hover:border-main/30 transition-all duration-200 overflow-hidden font-quicksand">
      <div className="absolute left-0 top-0 bottom-0 w-1 bg-gradient-to-b from-main to-[#0e4546]" />

      <button
        type="button"
        onClick={onOpen}
        className="w-full text-left p-4 pl-5"
      >
        <div className="flex items-start gap-3">
          <div className="shrink-0 w-11 h-11 rounded-xl bg-gradient-to-br from-main to-[#0e4546] text-white font-bold text-sm flex items-center justify-center shadow-soft">
            {getInitials(row.partyName)}
          </div>

          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <p className="text-[10px] font-bold uppercase tracking-wide text-muted">
                  {getLoanTypeLabel(row)}
                </p>
                <h3 className="font-bold text-ink text-base leading-tight truncate">
                  {row.partyName || "—"}
                </h3>
                {row.phone && (
                  <p className="flex items-center gap-1 text-xs text-muted mt-1">
                    <FiPhone size={11} className="shrink-0" />
                    <span className="truncate">{row.phone}</span>
                  </p>
                )}
              </div>

              <div className="text-right shrink-0">
                <p className="text-[10px] uppercase tracking-wide text-muted font-semibold">
                  Balance
                </p>
                <p className="text-lg font-extrabold text-danger leading-tight tabular-nums">
                  {fmtRs(balance)}
                </p>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-2 mt-3 text-xs">
              <div className="rounded-lg bg-subtle px-2.5 py-2">
                <p className="text-muted">Given</p>
                <p className="font-bold tabular-nums text-ink">
                  {fmtRs(principal)}
                </p>
              </div>
              <div className="rounded-lg bg-subtle px-2.5 py-2">
                <p className="text-muted">Received</p>
                <p className="font-bold tabular-nums text-success">
                  {fmtRs(paid)}
                </p>
              </div>
            </div>

            <div className="mt-3">
              <div className="flex justify-between text-[10px] text-muted mb-1">
                <span>Repaid {paidPct}%</span>
                <span>Last loan {formatDate(row.date)}</span>
              </div>
              <div className="h-1.5 rounded-full bg-subtle overflow-hidden">
                <div
                  className="h-full rounded-full bg-main transition-all"
                  style={{ width: `${paidPct}%` }}
                />
              </div>
            </div>
          </div>

          <FiChevronRight
            className="shrink-0 text-muted group-hover:text-main mt-1"
            size={20}
            aria-hidden
          />
        </div>
      </button>

      <div className="px-4 pl-5 pb-4 pt-3 flex items-center justify-between gap-2 border-t border-line mx-4">
        <span
          className={`text-[10px] font-bold px-2 py-0.5 rounded-full border ${statusClass}`}
        >
          {status}
        </span>
        {onEdit && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onEdit(row);
            }}
            className="inline-flex items-center gap-1.5 text-xs font-semibold text-main hover:underline px-2 py-1"
          >
            <FiEdit2 size={14} />
            Edit party
          </button>
        )}
      </div>
    </article>
  );
};

export default LoanPartyCard;
